import { useEffect, useMemo, useState } from 'react';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { LocationSelect } from '../../Components/Select/LocationSelect';
import { CompanySelect } from '../../Components/Select/CompanySelect';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormRow,
  FormTextField,
} from '../../Components/Form';
import { BIN_FIELD_LIMITS, buildBinLocationName } from './binLocationName';

export interface BinFormInput {
  ailse: string;
  bin_location_name?: string;
  rack: string | null;
  shelf: string | null;
  bin: string | null;
  ovid?: number;
  customer_id?: number;
}

export const BinForm: React.FC<{
  onSubmit: (data: BinFormInput) => void;
  onCancel: () => void;
  defaultValues?: BinFormInput;
  ovs: any[];
}> = ({ onSubmit, onCancel, defaultValues }) => {
  const [formData, setFormData] = useState<BinFormInput>({
    ailse: '',
    rack: '',
    shelf: '',
    bin: '',
  });

  useEffect(() => {
    if (defaultValues) {
      setFormData({
        ailse: defaultValues.ailse || '',
        rack: defaultValues.rack || '',
        shelf: defaultValues.shelf || '',
        bin: defaultValues.bin || '',
      });
    }
  }, [defaultValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? null : value,
    }));
  };

  // Derived live from aisle/rack/shelf/bin — never typed by the user, and
  // re-derived server-side on save so the two can't drift apart.
  const binLocationName = useMemo(() => buildBinLocationName(formData), [formData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const [selectedOvs, setSelectedOvs] = useState<string>('');
  const [CID] = useGetCustomData('/api/company/userregister');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [data] = useGetCustomData(`/api/location/${selectedCompany}`);

  // Only locations that actually use bins can receive one — same restriction as
  // the old app's /ovadmin/getactivebinvendinglocation endpoint.
  const binEnabledLocations = useMemo(
    () => (Array.isArray(data) ? data.filter((ov: any) => Number(ov.binlocation_flag) === 1 && Number(ov.IsActive) === 1) : []),
    [data]
  );

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      title={defaultValues ? 'Edit Bin Location' : 'Add Bin Location'}
      subtitle="Define aisle, rack, shelf, and bin identifiers for warehouse storage."
      submitLabel="Save Bin"
      onCancel={onCancel}
    >
      {!defaultValues && (
        <FormSection>
          <SectionTitle>Assignment</SectionTitle>
          <SectionDescription>Select the company and location for this bin.</SectionDescription>
          <FormRow>
            <CompanySelect
              required
              companies={CID}
              defaultName="Select Company"
              selectedCompany={selectedCompany}
              onChange={(newCompany: any) => {
                setSelectedCompany(newCompany);
                setFormData((prev: any) => ({
                  ...prev,
                  customer_id: Number(newCompany),
                  ovid: '',
                }));
                setSelectedOvs('');
              }}
            />
            <LocationSelect
              required
              ovs={binEnabledLocations}
              selectedOvs={selectedOvs}
              defaultName="Select Location"
              onChange={(newOvs: any) => {
                setSelectedOvs(newOvs);
                setFormData((prev) => ({
                  ...prev,
                  ovid: Number(newOvs),
                }));
              }}
              disabled={!selectedCompany}
            />
          </FormRow>
          {selectedCompany && binEnabledLocations.length === 0 && (
            <SectionDescription>
              This account has no bin-enabled locations. Turn on bin locations for a location first.
            </SectionDescription>
          )}
        </FormSection>
      )}

      <FormSection>
        <SectionTitle>Bin Details</SectionTitle>
        <SectionDescription>
          Enter the physical location identifiers. The bin location name is generated from these values.
        </SectionDescription>
        <FormGrid>
          <FormTextField
            label="Aisle"
            name="ailse"
            value={formData.ailse ?? ''}
            onChange={handleChange}
            required
            inputProps={{ maxLength: BIN_FIELD_LIMITS.ailse }}
            helperText={`Required, max ${BIN_FIELD_LIMITS.ailse} characters`}
          />
          <FormTextField
            label="Rack"
            name="rack"
            value={formData.rack ?? ''}
            onChange={handleChange}
            inputProps={{ maxLength: BIN_FIELD_LIMITS.rack }}
            helperText={`Optional, max ${BIN_FIELD_LIMITS.rack} characters`}
          />
          <FormTextField
            label="Shelf"
            name="shelf"
            value={formData.shelf ?? ''}
            onChange={handleChange}
            inputProps={{ maxLength: BIN_FIELD_LIMITS.shelf }}
            helperText={`Optional, max ${BIN_FIELD_LIMITS.shelf} characters`}
          />
          <FormTextField
            label="Bin"
            name="bin"
            value={formData.bin ?? ''}
            onChange={handleChange}
            inputProps={{ maxLength: BIN_FIELD_LIMITS.bin }}
            helperText={`Optional, max ${BIN_FIELD_LIMITS.bin} characters`}
          />
          <FormTextField
            label="Bin Location Name"
            name="bin_location_name"
            value={binLocationName}
            placeholder="Generated from Aisle"
            // Display only — derived here for preview and derived again
            // server-side on save, so it can never be set by hand.
            InputProps={{ readOnly: true }}
            inputProps={{ tabIndex: -1, 'aria-readonly': true }}
            helperText="Generated automatically as Aisle-Rack-Shelf-Bin"
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: 'action.disabledBackground',
                cursor: 'not-allowed',
              },
              '& .MuiInputBase-input': {
                cursor: 'not-allowed',
                color: 'text.secondary',
              },
            }}
          />
        </FormGrid>
      </FormSection>
    </Form>
  );
};

export default BinForm;
